import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { apiFetch } from "@/common/lib/api";
import Spinner from "@/common/components/Spinner";
import { BRIDGE_REQUIRED_PARAMS } from "@/common/utils/bridge-constants";
import { useDelayedFlag } from "@/state/hooks/useDelayedFlag";

const REQUIRED_PARAMS = BRIDGE_REQUIRED_PARAMS;

/** Allowed redirect_uri hosts for bridge OAuth (loopback only). */
const ALLOWED_REDIRECT_HOSTS = ["localhost", "127.0.0.1", "[::1]"];

/**
 * EN-1053: how long to wait for the loopback hand-off before offering a manual
 * continue. Generous on purpose, and suppressed outright once `pagehide` confirms
 * the browser is actually leaving (see the `leaving` state below) — but a redirect
 * that's slow to even start (e.g. a loopback listener slow to accept the connection)
 * can still race this timeout; there's no signal that distinguishes that case.
 */
const LOOPBACK_REDIRECT_TIMEOUT_MS = 6000;

function isAllowedRedirectUri(uri: string): boolean {
    try {
        const u = new URL(uri);
        return (u.protocol === "http:" || u.protocol === "https:") && ALLOWED_REDIRECT_HOSTS.includes(u.hostname);
    } catch {
        return false;
    }
}

interface BridgeCodeResponse {
    code: string;
}

/**
 * Bridge OAuth callback (authenticated).
 * User has already logged in; we exchange for a one-time auth code and redirect
 * the browser to the bridge's loopback URL with the code.
 */
export default function BridgeAuthCallback() {
    const [searchParams] = useSearchParams();
    const [error, setError] = useState<string | null>(null);
    const [redirectedTo, setRedirectedTo] = useState<string | null>(null);
    const [leaving, setLeaving] = useState(false);
    const [retryKey, setRetryKey] = useState(0);
    const timedOut = useDelayedFlag(redirectedTo !== null, LOOPBACK_REDIRECT_TIMEOUT_MS);
    const pendingContinueUrl = timedOut && !leaving ? redirectedTo : null;

    // PR review (EN-1053): the timeout alone can't tell a genuinely blocked hand-off apart
    // from one that's just slow (e.g. the loopback listener is slow to accept). `pagehide`
    // fires once the browser has actually committed to leaving — when it does, never show
    // the "stuck" fallback, even if it fires right around the same moment as the timeout.
    //
    // `pagehide` also fires when the browser freezes this page into the back-forward cache
    // rather than actually unloading it, and a later restore delivers `pageshow` with
    // `persisted: true` and resumes the exact same React tree — no remount, no effect rerun.
    // Without handling that, `leaving` would stay true forever and a user who hits Back
    // after a failed/abandoned hand-off would be stuck on the spinner with no way out.
    // Bumping `retryKey` re-triggers the code-exchange effect below from scratch.
    useEffect(() => {
        const markLeaving = () => setLeaving(true);
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                setLeaving(false);
                setError(null);
                setRedirectedTo(null);
                setRetryKey((key) => key + 1);
            }
        };
        window.addEventListener("pagehide", markLeaving);
        window.addEventListener("pageshow", handlePageShow);
        return () => {
            window.removeEventListener("pagehide", markLeaving);
            window.removeEventListener("pageshow", handlePageShow);
        };
    }, []);

    useEffect(() => {
        const redirectUri = searchParams.get("redirect_uri");
        const codeChallenge = searchParams.get("code_challenge");
        const codeChallengeMethod = searchParams.get("code_challenge_method");

        const missing = REQUIRED_PARAMS.filter((key) => !searchParams.get(key));
        if (missing.length > 0) {
            setError(`Missing required parameters: ${missing.join(", ")}`);
            return;
        }

        if (codeChallengeMethod !== "S256") {
            setError("code_challenge_method must be S256");
            return;
        }

        if (!redirectUri || !isAllowedRedirectUri(redirectUri)) {
            setError("redirect_uri must be a loopback URL (e.g. http://127.0.0.1:port/callback)");
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const res = await apiFetch<BridgeCodeResponse>("auth/bridge-code", {
                    method: "POST",
                    json: {
                        redirect_uri: redirectUri,
                        code_challenge: codeChallenge,
                        code_challenge_method: codeChallengeMethod,
                    },
                });

                if (cancelled) return;

                if (!res?.code) {
                    setError("Failed to get authorization code");
                    return;
                }

                const url = new URL(redirectUri);
                url.searchParams.set("code", res.code);
                const finalUrl = url.toString();
                window.location.href = finalUrl;

                // EN-1053: this navigation hands off to the bridge's local loopback listener.
                // If it's silently blocked, the user is stuck on the spinner forever with no
                // way out. useDelayedFlag(redirectedTo !== null, ...) offers a manual continue
                // link once we're still mounted this long after the hand-off was attempted.
                if (!cancelled) setRedirectedTo(finalUrl);
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : "Failed to complete sign-in for desktop app");
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [searchParams, retryKey]);

    if (error) {
        return (
            <div className="flex min-h-screen flex-1 flex-col items-center justify-center px-6 py-12">
                <p className="text-center text-gray-900">{error}</p>
            </div>
        );
    }

    if (pendingContinueUrl) {
        return (
            <div className="flex min-h-screen flex-1 flex-col items-center justify-center px-6 py-12">
                <p className="text-center text-gray-900">Almost done — click Continue to finish connecting the desktop app.</p>
                {/* PR review: a plain <a href> would sit the one-time code in the DOM as an
                    inspectable attribute for as long as this screen is shown (status-bar
                    preview, DOM-reading extensions, screen-sharing). A button that navigates
                    on click keeps the URL in memory until the moment it's actually used. */}
                <button
                    type="button"
                    className="mt-4 text-teal-600 underline"
                    onClick={() => {
                        window.location.href = pendingContinueUrl;
                    }}
                >
                    Continue
                </button>
                <p className="mt-6 text-center text-gray-600">
                    Once the desktop app confirms, <strong>you can close this tab</strong>.
                </p>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen flex-1 flex-col items-center justify-center px-6 py-12">
            <Spinner />
            <p className="mt-4 text-center text-gray-600">Completing sign-in for desktop app…</p>
        </div>
    );
}
